import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import { toast } from "sonner";
import {
  MessageSquare,
  Bug,
  Lightbulb,
  Clock,
  RefreshCw,
  CheckCircle2,
  AlertCircle,
  PauseCircle,
  PlayCircle,
  Globe,
  Server,
  Layers,
  User as UserIcon,
  ChevronRight,
  CalendarDays,
  Inbox,
  Quote,
} from "lucide-react";
import api from "../lib/api";
import { cn } from "../lib/utils";

// Status maps — partners see human-friendly labels, not internal slugs.
const STATUS_META = {
  backlog: { label: "Logged", icon: PauseCircle, color: "text-zinc-300", bg: "bg-zinc-700/30 border-zinc-600/50", banner: "bg-zinc-700/20 border-zinc-700" },
  todo: { label: "Planned", icon: PlayCircle, color: "text-blue-300", bg: "bg-blue-500/15 border-blue-500/30", banner: "bg-blue-900/30 border-blue-700/50" },
  in_progress: { label: "In progress", icon: Clock, color: "text-amber-300", bg: "bg-amber-500/15 border-amber-500/30", banner: "bg-amber-900/30 border-amber-700/50" },
  done: { label: "Shipped", icon: CheckCircle2, color: "text-emerald-300", bg: "bg-emerald-500/15 border-emerald-500/30", banner: "bg-emerald-900/30 border-emerald-700/50" },
  rejected: { label: "Won't ship", icon: AlertCircle, color: "text-red-300", bg: "bg-red-500/15 border-red-500/30", banner: "bg-red-900/30 border-red-700/50" },
};
const PRIORITY_META = {
  p0: { label: "P0 — urgent", color: "text-red-300 border-red-500/40 bg-red-500/10" },
  p1: { label: "P1 — high", color: "text-orange-300 border-orange-500/40 bg-orange-500/10" },
  p2: { label: "P2 — medium", color: "text-amber-300 border-amber-500/40 bg-amber-500/10" },
  p3: { label: "P3 — low", color: "text-zinc-400 border-zinc-600/40 bg-zinc-700/20" },
  high: { label: "High", color: "text-orange-300 border-orange-500/40 bg-orange-500/10" },
  normal: { label: "Normal", color: "text-zinc-300 border-zinc-600/40 bg-zinc-700/20" },
  low: { label: "Low", color: "text-zinc-400 border-zinc-700/40 bg-zinc-800/30" },
};

const TYPE_ICON = { bug: Bug, feature: Lightbulb };

// Team sentiment — kept in sync with DevBacklogPage's SENTIMENT_CONFIG.
// Partners see this as the team's verdict on whether the idea has legs.
const SENTIMENT_META = {
  fire:      { emoji: "🔥", label: "Fire — building it",  color: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40" },
  good:      { emoji: "👍", label: "Good idea",           color: "bg-blue-500/15 text-blue-300 border-blue-500/40" },
  neutral:   { emoji: "🤔", label: "Considering",         color: "bg-amber-500/15 text-amber-300 border-amber-500/40" },
  weak:      { emoji: "👎", label: "Weak fit",            color: "bg-orange-500/15 text-orange-300 border-orange-500/40" },
  wont_ship: { emoji: "🚫", label: "Won't ship",          color: "bg-red-500/15 text-red-300 border-red-500/40" },
};

const fmtDate = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }); } catch { return iso; }
};
const fmtDateTime = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

const MyFeedbackPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  // Partners chat across many Vexos deployments per week — without this
  // filter their /my-feedback list mixes feedback for Ola, Ocean Cove,
  // Modernest WeHo, etc., and they can't tell which complaint applies
  // to which property.
  const [instanceFilter, setInstanceFilter] = useState("all");
  const [selected, setSelected] = useState(null); // detail dialog state
  // Comment composer state for the active dialog.
  const [commentDraft, setCommentDraft] = useState("");
  const [postingComment, setPostingComment] = useState(false);

  const fetchItems = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.get("/my-feedback");
      setItems(data || []);
    } catch (e) {
      toast.error("Failed to load feedback");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const postComment = useCallback(async () => {
    if (!selected || !commentDraft.trim() || postingComment) return;
    try {
      setPostingComment(true);
      const { data: comment } = await api.post(`/my-feedback/${selected.id}/comment`, {
        content: commentDraft.trim(),
      });
      // Optimistic update — append the comment to the selected item AND
      // the master list so the dialog reflects immediately and the list
      // card preview will catch the next time it renders.
      const next = {
        ...selected,
        comments: [...(selected.comments || []), comment],
        updated_at: comment.created_at,
      };
      setSelected(next);
      setItems((prev) => prev.map((it) => (it.id === selected.id ? next : it)));
      setCommentDraft("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to post reply");
    } finally {
      setPostingComment(false);
    }
  }, [selected, commentDraft, postingComment]);

  const filtered = useMemo(() => {
    return items.filter((i) => {
      if (statusFilter !== "all" && (i.status || "backlog") !== statusFilter) return false;
      if (instanceFilter !== "all" && (i.product_instance_slug || "") !== instanceFilter) return false;
      return true;
    });
  }, [items, statusFilter, instanceFilter]);

  const counts = useMemo(() => {
    const out = { all: items.length };
    items.forEach((i) => {
      const s = i.status || "backlog";
      out[s] = (out[s] || 0) + 1;
    });
    return out;
  }, [items]);

  // Distinct deployments the partner has filed feedback against — only
  // render the deployment chip-strip when there's actual variety to filter.
  const deployments = useMemo(() => {
    const seen = new Map();
    items.forEach((i) => {
      const slug = i.product_instance_slug;
      if (!slug) return;
      if (!seen.has(slug)) {
        seen.set(slug, {
          slug,
          label: i.product_instance_label || slug,
          count: 1,
        });
      } else {
        seen.get(slug).count += 1;
      }
    });
    return Array.from(seen.values());
  }, [items]);

  return (
    <div className="space-y-5 p-6 max-w-5xl" data-testid="my-feedback-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-zinc-50 flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-orange-400" /> My feedback
          </h1>
          <p className="text-sm text-zinc-500 max-w-2xl">
            Every idea or bug you've filed via the AI chat that the team logged.
            You'll see how we categorized it, how we prioritized it, our ETA,
            and our reasoning — especially for things we won't ship and why.
            <span className="block mt-1 text-zinc-600 text-xs">
              Click any card to see the full context and the team's response.
            </span>
          </p>
        </div>
        <Button variant="outline" size="sm" className="border-zinc-700" onClick={fetchItems}>
          <RefreshCw className="w-4 h-4 mr-1" /> Refresh
        </Button>
      </div>

      {/* Deployment filter — only renders when the partner has feedback
          across 2+ deployments (one is no choice). */}
      {deployments.length >= 2 && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[10px] text-zinc-500 uppercase tracking-wide flex items-center gap-1">
            <Server className="w-2.5 h-2.5" /> Deployment:
          </span>
          <button
            type="button"
            onClick={() => setInstanceFilter("all")}
            className={cn(
              "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border text-xs transition-colors",
              instanceFilter === "all"
                ? "bg-zinc-700/40 border-zinc-600/50 text-zinc-200"
                : "border-zinc-700 bg-zinc-900/40 text-zinc-400 hover:bg-zinc-800/60"
            )}
          >
            All
            <span className="text-[10px] opacity-70">{items.length}</span>
          </button>
          {deployments.map((d) => (
            <button
              key={d.slug}
              type="button"
              onClick={() => setInstanceFilter(d.slug)}
              className={cn(
                "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border text-xs transition-colors",
                instanceFilter === d.slug
                  ? "bg-orange-500/15 border-orange-500/40 text-orange-200"
                  : "border-zinc-700 bg-zinc-900/40 text-zinc-400 hover:bg-zinc-800/60"
              )}
            >
              {d.label}
              <span className="text-[10px] opacity-70">{d.count}</span>
            </button>
          ))}
        </div>
      )}

      {/* Status filter chips */}
      <div className="flex gap-1.5 flex-wrap">
        {["all", "backlog", "todo", "in_progress", "done", "rejected"].map((s) => {
          const m = s === "all"
            ? { label: "All", icon: null, color: "text-zinc-200", bg: "bg-zinc-700/40 border-zinc-600/50" }
            : STATUS_META[s] || { label: s, icon: null, color: "text-zinc-400", bg: "bg-zinc-800 border-zinc-700" };
          const Icon = m.icon;
          const n = counts[s] || 0;
          return (
            <button
              key={s}
              type="button"
              onClick={() => setStatusFilter(s)}
              className={cn(
                "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border text-xs transition-colors",
                statusFilter === s
                  ? `${m.bg} ${m.color}`
                  : "border-zinc-700 bg-zinc-900/40 text-zinc-400 hover:bg-zinc-800/60"
              )}
            >
              {Icon && <Icon className="w-3 h-3" />}
              {m.label}
              <span className="text-[10px] opacity-70">{n}</span>
            </button>
          );
        })}
      </div>

      {loading ? (
        <p className="text-sm text-zinc-500 text-center py-8">Loading…</p>
      ) : filtered.length === 0 ? (
        <Card className="bg-zinc-900/40 border-zinc-800">
          <CardContent className="p-8 text-center text-sm text-zinc-500">
            {items.length === 0
              ? <>Nothing logged yet. Use the AI chat widget (bottom-right) to file something.</>
              : <>No items in this status. Pick another tab.</>
            }
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2.5">
          {filtered.map((item) => {
            const status = STATUS_META[item.status || "backlog"] || STATUS_META.backlog;
            const StatusIcon = status.icon;
            const effectiveType = item.admin_category_override || item.type || "feature";
            const TypeIcon = TYPE_ICON[effectiveType] || Lightbulb;
            const priorityKey = item.admin_priority || item.priority || "normal";
            const priorityMeta = PRIORITY_META[priorityKey] || PRIORITY_META.normal;
            const reviewed = !!item.admin_reviewed_at;
            return (
              <Card
                key={item.id}
                onClick={() => setSelected(item)}
                className={cn(
                  "bg-zinc-900/40 border-zinc-800 hover:border-orange-500/50 hover:bg-zinc-900/70 transition-colors cursor-pointer group",
                  item.status === "rejected" && "opacity-80"
                )}
              >
                <CardContent className="p-4 space-y-2.5">
                  {/* Top row: type icon, title, status chip, chevron */}
                  <div className="flex items-start gap-2">
                    <TypeIcon className={cn(
                      "w-4 h-4 mt-1 flex-shrink-0",
                      effectiveType === "bug" ? "text-red-400" : "text-amber-400"
                    )} />
                    <h3 className="text-sm font-semibold text-zinc-100 flex-1 min-w-0">
                      {item.title}
                    </h3>
                    <span className={cn(
                      "inline-flex items-center gap-1 px-2 py-0.5 rounded border text-[10px] flex-shrink-0",
                      status.bg, status.color
                    )}>
                      {StatusIcon && <StatusIcon className="w-3 h-3" />}
                      {status.label}
                    </span>
                    <ChevronRight className="w-4 h-4 text-zinc-600 group-hover:text-orange-400 transition-colors flex-shrink-0" />
                  </div>

                  {/* Compact meta row */}
                  <div className="flex items-center gap-1.5 flex-wrap pl-6">
                    {/* Team sentiment chip — most prominent signal so the
                        partner sees at a glance whether the team likes
                        the idea ("🔥 Fire") or rejected it ("🚫 Won't ship"). */}
                    {(() => {
                      const sentiment = item.team_sentiment?.rating;
                      const meta = sentiment && SENTIMENT_META[sentiment];
                      if (!meta) return null;
                      return (
                        <Badge variant="outline" className={cn("text-[10px] px-1.5 py-0", meta.color)}>
                          <span className="mr-0.5">{meta.emoji}</span> {meta.label}
                        </Badge>
                      );
                    })()}
                    <Badge variant="outline" className={cn("text-[10px] px-1.5 py-0", priorityMeta.color)}>
                      {priorityMeta.label}
                    </Badge>
                    {item.admin_estimated_time && (
                      <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-emerald-500/40 text-emerald-300 bg-emerald-500/10">
                        <Clock className="w-2.5 h-2.5 mr-0.5" /> ETA: {item.admin_estimated_time}
                      </Badge>
                    )}
                    {item.product_instance_label && (
                      <span className="text-[10px] text-zinc-500 inline-flex items-center gap-0.5">
                        <Server className="w-2.5 h-2.5" /> {item.product_instance_label}
                      </span>
                    )}
                    {!reviewed && (
                      <span className="text-[10px] italic text-amber-400/80 ml-auto">
                        Awaiting team review
                      </span>
                    )}
                  </div>

                  {/* One-line description preview + screenshot thumb if any */}
                  <div className="pl-6 flex items-start gap-2">
                    <p className="text-xs text-zinc-500 line-clamp-2 flex-1">
                      {item.description || (item.image_url ? "(screenshot attached)" : "")}
                    </p>
                    {item.image_url && (
                      <img
                        src={item.image_url}
                        alt=""
                        onClick={(e) => e.stopPropagation()}
                        className="w-12 h-12 object-cover rounded border border-zinc-700 flex-shrink-0"
                        onError={(e) => { e.currentTarget.style.display = "none"; }}
                      />
                    )}
                  </div>

                  {/* If admin replied, hint with first words */}
                  {item.admin_reasoning && (
                    <div className="pl-6 text-[11px] text-zinc-300 line-clamp-1">
                      <span className="text-zinc-500 mr-1">Team:</span> {item.admin_reasoning}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* ─────────────── Detail dialog ─────────────── */}
      <Dialog
        open={!!selected}
        onOpenChange={(open) => {
          if (!open) {
            setSelected(null);
            setCommentDraft("");
          }
        }}
      >
        <DialogContent className="bg-zinc-950 border-zinc-800 max-w-2xl max-h-[85vh] overflow-y-auto">
          {selected && (() => {
            const status = STATUS_META[selected.status || "backlog"] || STATUS_META.backlog;
            const StatusIcon = status.icon;
            const effectiveType = selected.admin_category_override || selected.type || "feature";
            const TypeIcon = TYPE_ICON[effectiveType] || Lightbulb;
            const priorityKey = selected.admin_priority || selected.priority || "normal";
            const priorityMeta = PRIORITY_META[priorityKey] || PRIORITY_META.normal;
            const reviewed = !!selected.admin_reviewed_at;
            return (
              <>
                <DialogHeader>
                  <DialogTitle className="text-zinc-100 text-lg flex items-start gap-2 pr-8">
                    <TypeIcon className={cn(
                      "w-5 h-5 mt-0.5 flex-shrink-0",
                      effectiveType === "bug" ? "text-red-400" : "text-amber-400"
                    )} />
                    <span className="flex-1">{selected.title}</span>
                  </DialogTitle>
                  <DialogDescription className="sr-only">
                    Feedback item details — status, priority, ETA, team response.
                  </DialogDescription>
                </DialogHeader>

                {/* Status banner — big visual cue at the top so partner immediately
                    sees if their item is logged / planned / shipped / rejected. */}
                <div className={cn(
                  "rounded-lg border px-4 py-3 flex items-center gap-3 mt-1",
                  status.banner
                )}>
                  {StatusIcon && <StatusIcon className={cn("w-6 h-6", status.color)} />}
                  <div className="flex-1">
                    <div className={cn("text-sm font-semibold", status.color)}>{status.label}</div>
                    <div className="text-[11px] text-zinc-400">
                      {selected.status === "rejected"
                        ? "We've reviewed this and decided not to ship — see the team's reasoning below."
                        : selected.status === "done"
                        ? "Shipped. You should see the change live."
                        : selected.status === "in_progress"
                        ? "The team is actively working on this."
                        : selected.status === "todo"
                        ? "On the roadmap. The ETA below is our best estimate."
                        : "Logged in our system. The team will review and prioritize."
                      }
                    </div>
                  </div>
                </div>

                {/* Team sentiment banner — separate from status because it
                    answers a different question. Status: "where in the
                    workflow is this?". Sentiment: "do we like the idea?".
                    Partners care about both — a "logged" status with a
                    "🔥 Fire" sentiment means we love it and it's coming;
                    "logged" + "👎 Weak fit" means it's noted but unlikely. */}
                {(() => {
                  const sentiment = selected.team_sentiment?.rating;
                  const meta = sentiment && SENTIMENT_META[sentiment];
                  if (!meta) return null;
                  return (
                    <div className={cn(
                      "rounded-lg border px-4 py-3 flex items-center gap-3 mt-2",
                      meta.color
                    )}>
                      <span className="text-2xl">{meta.emoji}</span>
                      <div className="flex-1">
                        <div className={cn("text-sm font-semibold", meta.color.split(" ").find((c) => c.startsWith("text-")))}>
                          {meta.label}
                        </div>
                        <div className="text-[11px] text-zinc-400">
                          {sentiment === "fire" && "The team is excited about this idea — we're prioritizing it."}
                          {sentiment === "good" && "The team thinks this is a solid idea — see ETA for timing."}
                          {sentiment === "neutral" && "The team is still discussing whether and when to build this."}
                          {sentiment === "weak" && "We're not convinced this is worth the trade-offs — see reasoning below."}
                          {sentiment === "wont_ship" && "The team has decided this won't ship — see reasoning below."}
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {/* Priority + ETA + Category strip — three big stat cards. */}
                <div className="grid grid-cols-3 gap-2 mt-3">
                  <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-2.5">
                    <div className="text-[10px] uppercase text-zinc-500">Priority</div>
                    <div className={cn("text-sm font-semibold mt-0.5", priorityMeta.color.split(" ").find(c => c.startsWith("text-")) || "text-zinc-200")}>
                      {priorityMeta.label}
                    </div>
                  </div>
                  <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-2.5">
                    <div className="text-[10px] uppercase text-zinc-500 flex items-center gap-1">
                      <Clock className="w-2.5 h-2.5" /> ETA
                    </div>
                    <div className="text-sm font-semibold mt-0.5 text-zinc-200">
                      {selected.admin_estimated_time || <span className="text-zinc-600 text-xs font-normal italic">not set yet</span>}
                    </div>
                  </div>
                  <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-2.5">
                    <div className="text-[10px] uppercase text-zinc-500">Type</div>
                    <div className="text-sm font-semibold mt-0.5 text-zinc-200 capitalize">
                      {effectiveType}
                      {selected.admin_category_override && selected.admin_category_override !== selected.type && (
                        <span className="text-[10px] text-violet-400 ml-1.5 font-normal">re-categorized</span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Deployment / mirror context */}
                {(selected.product_name || selected.product_instance_label || selected.product_instance_mirror_label) && (
                  <div className="mt-3 flex items-center gap-2 text-[11px] text-zinc-400 flex-wrap">
                    <span className="text-zinc-500 uppercase text-[10px]">Where:</span>
                    {selected.product_name && (
                      <span className="inline-flex items-center gap-1"><Globe className="w-3 h-3" />{selected.product_name}</span>
                    )}
                    {selected.product_instance_label && (
                      <span className="inline-flex items-center gap-1"><Server className="w-3 h-3" />{selected.product_instance_label}</span>
                    )}
                    {selected.product_instance_mirror_label && (
                      <span className="inline-flex items-center gap-1"><Layers className="w-3 h-3" />{selected.product_instance_mirror_label}</span>
                    )}
                  </div>
                )}

                {/* What you wrote — original message in a quote-style block,
                    plus the screenshot the partner attached if any. */}
                <div className="mt-4">
                  <div className="text-[10px] uppercase tracking-wide text-zinc-500 mb-1.5 flex items-center gap-1">
                    <Quote className="w-2.5 h-2.5" /> What you wrote
                  </div>
                  <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-3 text-xs text-zinc-300 leading-relaxed whitespace-pre-wrap">
                    {selected.description
                      ? selected.description
                      : selected.image_url
                        ? <span className="italic text-zinc-500">(screenshot attached — see below)</span>
                        : <span className="italic text-zinc-600">No description captured.</span>
                    }
                  </div>
                  {selected.image_url && (
                    <a
                      href={selected.image_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-2 block"
                    >
                      <img
                        src={selected.image_url}
                        alt="Attached screenshot"
                        className="max-h-80 max-w-full rounded-lg border border-zinc-700 hover:border-orange-500 transition-colors"
                        onError={(e) => { e.currentTarget.style.display = "none"; }}
                      />
                    </a>
                  )}
                </div>

                {/* Team's response — admin reasoning. The whole reason this page
                    exists. Render BIG when present, with a clear "awaiting"
                    placeholder when not. */}
                <div className="mt-4">
                  <div className="text-[10px] uppercase tracking-wide text-zinc-500 mb-1.5 flex items-center gap-1.5">
                    <UserIcon className="w-2.5 h-2.5" /> Team's response
                    {selected.admin_reviewed_by && (
                      <span className="normal-case text-zinc-600">— {selected.admin_reviewed_by}</span>
                    )}
                  </div>
                  {selected.admin_reasoning ? (
                    <div className="rounded-lg border border-orange-500/30 bg-orange-950/10 p-3 text-sm text-zinc-200 leading-relaxed whitespace-pre-wrap">
                      {selected.admin_reasoning}
                    </div>
                  ) : (
                    <div className="rounded-lg border border-zinc-800 bg-zinc-900/30 p-3 text-xs text-zinc-500 italic flex items-center gap-2">
                      <Inbox className="w-3.5 h-3.5" />
                      Awaiting team review. We'll respond here once we've looked at this.
                    </div>
                  )}
                </div>

                {/* Two-sided comment thread — partner can push back on the
                    team's verdict, ask follow-up questions, agree, etc. Team
                    posts via /dev-backlog/{id}/comment from the admin side;
                    partner posts via /my-feedback/{id}/comment. Each shows
                    role-coded bubbles + an inline composer. */}
                <div className="mt-4 border-t border-zinc-800/60 pt-3">
                  <div className="text-[10px] uppercase tracking-wide text-zinc-500 mb-2 flex items-center gap-1">
                    <MessageSquare className="w-2.5 h-2.5" /> Conversation
                  </div>
                  <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
                    {(selected.comments || []).length === 0 ? (
                      <p className="text-[11px] text-zinc-600 italic">No replies yet — start the conversation below.</p>
                    ) : (
                      (selected.comments || []).map((c) => (
                        <div
                          key={c.id}
                          className={cn(
                            "flex gap-2",
                            c.role === "partner" ? "justify-end" : "justify-start"
                          )}
                        >
                          <div
                            className={cn(
                              "max-w-[85%] rounded-lg px-3 py-2 text-xs leading-relaxed whitespace-pre-wrap",
                              c.role === "partner"
                                ? "bg-blue-500/15 border border-blue-500/30 text-blue-100"
                                : "bg-orange-500/15 border border-orange-500/30 text-orange-100"
                            )}
                          >
                            <div className="flex items-center gap-1.5 mb-1">
                              <span className={cn(
                                "text-[10px] font-medium uppercase tracking-wide",
                                c.role === "partner" ? "text-blue-300" : "text-orange-300"
                              )}>
                                {c.role === "partner" ? "You" : (c.author_name || "Team")}
                              </span>
                              <span className="text-[10px] text-zinc-500">
                                {fmtDateTime(c.created_at)}
                              </span>
                            </div>
                            {c.content}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                  {/* Composer — partners can reply / disagree / push back. */}
                  <div className="mt-2 flex gap-2">
                    <textarea
                      value={commentDraft}
                      onChange={(e) => setCommentDraft(e.target.value)}
                      placeholder={
                        selected.status === "rejected"
                          ? "Disagree with our verdict? Tell us why."
                          : "Reply to the team — questions, follow-ups, more context…"
                      }
                      className="flex-1 text-xs bg-zinc-900 border border-zinc-700 rounded px-2 py-1.5 text-zinc-100 placeholder:text-zinc-600 resize-y min-h-[60px]"
                      onKeyDown={async (e) => {
                        if ((e.metaKey || e.ctrlKey) && e.key === "Enter" && commentDraft.trim() && !postingComment) {
                          e.preventDefault();
                          await postComment();
                        }
                      }}
                    />
                    <Button
                      size="sm"
                      className="bg-orange-500 hover:bg-orange-600 self-end"
                      disabled={!commentDraft.trim() || postingComment}
                      onClick={postComment}
                    >
                      {postingComment ? "Sending…" : "Send"}
                    </Button>
                  </div>
                  <p className="text-[10px] text-zinc-600 mt-1">⌘/Ctrl + Enter to send</p>
                </div>

                {/* Timeline — when filed → reviewed → status changed */}
                <div className="mt-4 border-t border-zinc-800/60 pt-3">
                  <div className="text-[10px] uppercase tracking-wide text-zinc-500 mb-2 flex items-center gap-1">
                    <CalendarDays className="w-2.5 h-2.5" /> Timeline
                  </div>
                  <div className="space-y-1.5 text-[11px] text-zinc-400">
                    <div className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-zinc-500" />
                      <span className="text-zinc-300">Filed</span>
                      <span className="text-zinc-500 ml-auto">{fmtDateTime(selected.created_at)}</span>
                    </div>
                    {reviewed && (
                      <div className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-orange-500" />
                        <span className="text-zinc-300">Reviewed by team</span>
                        <span className="text-zinc-500 ml-auto">{fmtDateTime(selected.admin_reviewed_at)}</span>
                      </div>
                    )}
                    {selected.updated_at && selected.updated_at !== selected.created_at && (
                      <div className="flex items-center gap-2">
                        <span className={cn("w-1.5 h-1.5 rounded-full", status.color.replace("text-", "bg-"))} />
                        <span className="text-zinc-300">Last updated</span>
                        <span className="text-zinc-500 ml-auto">{fmtDateTime(selected.updated_at)}</span>
                      </div>
                    )}
                  </div>
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MyFeedbackPage;
